/**
 * AI配置面板状态管理Hook
 * 职责: 管理AI配置的加载和CRUD操作
 * 限制: <150行
 */

import { useState, useCallback } from 'react';
import { App } from 'antd';
import {
  UserPreference,
} from '../../../types/ai-analysis-types';
import {
  getSKUAIConfigs,
  createSKUAIConfig,
  updateSKUAIConfig,
  deleteSKUAIConfig,
  setDefaultConfig,
  cloneConfig,
} from '../../../services/skuAIConfigService';

interface UseAIConfigPanelProps {
  skuId: number;
  onConfigChange?: (config: UserPreference | null) => void;
  setCurrentConfig: (config: UserPreference | null) => void;
}

export const useAIConfigPanel = ({ skuId, onConfigChange, setCurrentConfig }: UseAIConfigPanelProps) => {
  const { message } = App.useApp();
  const [configs, setConfigs] = useState<UserPreference[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState<UserPreference | null>(null);

  // 加载配置列表
  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const configsList = await getSKUAIConfigs(skuId);
      const configsArray = Array.isArray(configsList) ? configsList : [];
      setConfigs(configsArray);

      const defaultConfig = configsArray.find(c => c.is_default);
      const selectedConfig = defaultConfig || configsArray[0] || null;
      
      setCurrentConfig(selectedConfig);
      onConfigChange?.(selectedConfig);
    } catch (error) {
      console.error('加载配置失败:', error);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [skuId, setCurrentConfig, onConfigChange]);

  // 保存配置
  const handleSave = async (values: any) => {
    try {
      if (editingConfig?.id) {
        await updateSKUAIConfig(skuId, editingConfig.id, values);
        message.success('配置已更新');
      } else {
        await createSKUAIConfig(skuId, values);
        message.success('配置已创建');
      }
      setModalVisible(false);
      loadConfigs();
    } catch (error) {
      message.error('保存失败');
    }
  };

  // 删除配置
  const handleDelete = async (configId: number) => {
    try {
      await deleteSKUAIConfig(skuId, configId);
      message.success('配置已删除');
      loadConfigs();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 设为默认
  const handleSetDefault = async (configId: number) => {
    try {
      await setDefaultConfig(skuId, configId);
      message.success('已设为默认配置');
      loadConfigs();
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 复制配置
  const handleClone = async (configId: number) => {
    try {
      const originalConfig = configs.find(c => c.id === configId);
      const newName = `${originalConfig?.config_name || '配置'} - 副本`;
      await cloneConfig(skuId, configId, newName);
      message.success('配置已复制');
      loadConfigs();
    } catch (error) {
      message.error('复制失败');
    }
  };

  // 打开新建配置
  const handleCreate = () => {
    setEditingConfig(null);
    setModalVisible(true);
  };

  // 打开编辑配置
  const handleEdit = (config: UserPreference) => {
    setEditingConfig(config);
    setModalVisible(true);
  };

  return {
    configs,
    loading,
    modalVisible,
    editingConfig,
    setModalVisible,
    loadConfigs,
    handleSave,
    handleDelete,
    handleSetDefault,
    handleClone,
    handleCreate,
    handleEdit,
  };
};

